/* DataTableRenderer.js */

/* global Slick */
"use strict";

const _ = require("lodash");
const ElementResizeDetectionUtility = require("@gluenetworks/dom_utilities").Resize
    .ElementResizeDetectionUtility;
const eventRegisterMixin = require("../../utils/eventRegisterMixin");

/**
 * @listens DataTableComponent#event:Title
 * @listens DataTableComponent#event:Data
 * @listens DataTableComponent#event:UpdateItem
 * @listens DataTableComponent#event:AddItem
 * @listens DataTableComponent#event:RemoveItem
 * @listens DataTableComponent#event:Controls
 * @listens DataTableComponent#event:Loading
 * @listens DataTableComponent#event:Selected
 *
 * @listens Slick.Grid:onClick
 * @listens Slick.Grid:onSelectedRowsChanged
 * @listens Slick.Data.DataView:onRowCountChanged
 */

const ACTION_UPDATE = "UPDATE";
const ACTION_ADD = "ADD";
const ACTION_REMOVE = "REMOVE";

class DatatableRenderer extends eventRegisterMixin() {
    constructor(model, isSelectable, noDataMessage, gridFiltering) {
        super();
        this._model = model;
        this._rootElem = this._createRootElement();
        this._noDataMessage = noDataMessage || "";
        this._gridFiltering = gridFiltering;

        this._isSelectable = !!isSelectable;
        this._tableTitle = this._createTitle();
        this._table = this._createTable();
        this._controlsContainer = this._createControlsContainer();

        this._controls = [];
        this._griOptions = {
            enableCellNavigation: true,
            enableRowSelection: true,
            enableAsyncPostRender: true,
            rowHeight: 25
        };

        this._maxVisibleRows = 5;
        this._noRowsHeight = 3;
        this.updateTableHeight(0);

        this._elementResizeDetectionUtility = new ElementResizeDetectionUtility();
        this._onResize = _.throttle(this._resizeCanvas.bind(this), 100);

        this.render();
        this._registerListeners();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    get grid() {
        return this._slickGrid;
    }

    get dataView() {
        return this._slickData;
    }
    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._registerListener(this._model, "Title", this._onTitleChange.bind(this));
        this._registerListener(this._model, "Data", this._onDataChange.bind(this));
        this._registerListener(this._model, "UpdateItem", this._onDataUpdate.bind(this, ACTION_UPDATE));
        this._registerListener(this._model, "AddItem", this._onDataUpdate.bind(this, ACTION_ADD));
        this._registerListener(this._model, "RemoveItem", this._onDataUpdate.bind(this, ACTION_REMOVE));
        this._registerListener(this._model, "Controls", this._onControls.bind(this));
        this._registerListener(this._model, "Loading", this._onLoading.bind(this));
        this._registerListener(this._model, "Selected", this._onSelected.bind(this));
        this._elementResizeDetectionUtility.addResizeListener(
            this._rootElem,
            this._onResize
        );
    }

    _unregisterListeners() {
        this._unregisterAllListeners();
        this._elementResizeDetectionUtility.removeResizeListener(
            this._rootElem,
            this._onResize
        );
    }

    _render() {
        this.checkIsEmpty();
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();
        root.appendChild(this._tableTitle);
        root.appendChild(this._table);
        root.appendChild(this._controlsContainer);
        this._table.appendChild(this._createNoDataPlaceholder(this._noDataMessage));

        this._resizeCanvas.bind(this);
        setTimeout(() => {
            this._initSlickGrid(
                this._table,
                this._model.columns,
                this._griOptions,
                this._isSelectable
            );
        }, 0);
        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _shutdown() {
        this._unregisterListeners();

        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            Promise.resolve(this._slickGrid.destroy()).catch(console.warn),
            this._elementResizeDetectionUtility.shutdown()
        ])
            .then(() => {
                this._rootElem = null;
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
                this._slickGrid = null;
                this._slickData = null;
                this._elementResizeDetectionUtility = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("data-table");
        rootElement.component = this._model;
        return rootElement;
    }

    _createTitle() {
        const tableTitle = document.createElement("div");
        tableTitle.classList.add("table-title");
        return tableTitle;
    }

    _createTable() {
        const tableData = document.createElement("div");
        tableData.classList.add("table");
        return tableData;
    }

    _createControlsContainer() {
        const controlsWrapper = document.createElement("div");
        controlsWrapper.classList.add("table-controls");
        return controlsWrapper;
    }

    _createNoDataPlaceholder(noDataMessage) {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = noDataMessage;
        return noDataPlaceholder;
    }
    /*******************************************/
    /* END PRIVATE RENDERER METHODS  */
    /* START HANDLER METHODS */
    /*******************************************/

    _onTitleChange(title) {
        this._tableTitle.innerText = title || "";
    }

    _onDataChange(data) {
        this._slickData.setItems(data);
    }
    _onDataUpdate(action, item) {
        switch (action) {
            case ACTION_UPDATE:
                this._slickData.updateItem(item.id, item);
                break;
            case ACTION_ADD: {
                const modelData = this._model.getData() || [];
                const position = modelData.findIndex((_item) => item.id === _item.id);
                if (position > -1) {
                    this._slickData.insertItem(position, item);
                } else {
                    this._slickData.addItem(item);
                }
                break;
            }
            case ACTION_REMOVE:
                this._slickData.deleteItem(item.id);
                break;
        }
    }

    _onControls(controls) {
        this._controls = controls;
        const controlsSet = controls.reduce((container, control) => {
            container.appendChild(control.renderer.rootElement);
            return container;
        }, document.createDocumentFragment());

        this._controlsContainer.innerHTML = "";
        this._controlsContainer.appendChild(controlsSet);
    }

    _onLoading(isLoading) {
        this._setLoading(isLoading);
    }

    _onWindowResize() {
        this._resizeCanvas();
    }

    _onRowCountChange(e, { current }) {
        this._slickGrid.invalidate();
        this._slickGrid.updateRowCount();

        this.updateTableHeight(current);
        this.checkIsEmpty();
    }

    _onRowsChanged(e, { rows }) {
        this._slickGrid.invalidateRows(rows);
        this._slickGrid.render();
    }

    _onGridClick(e, { row /*, cell*/, grid }) {
        const target = e.target;
        const data = grid.getData();
        if (this._isSelectable) {
            this._model.setSelected(row, data.getItem(row));
        }

        if (target.getAttribute("role") === "button") {
            const actionHandler = this._model.actions[target.dataset.action];
            const actionData = target.dataset.actionData;
            if (typeof actionHandler === "function") {
                actionHandler(actionData, data.getItem(row));
            }
        }
    }

    _onSelected({ rows }) {
        this._slickGrid.setSelectedRows(rows);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _initSlickGrid(container, columns, options = {}, isSelectable = false) {
        console.log(columns);
        const {args: filterArgs, filterFn} = this._gridFiltering || {};

        const slickData = new Slick.Data.DataView();
        const slickGrid = new Slick.Grid(container, slickData, columns, options);

        //set Grid Filtering if needed
        if (filterArgs) {
            slickData.setFilterArgs(filterArgs);
        }
        if (filterFn) {
            slickData.setFilter(filterFn);
        }

        slickData.getItemMetadata = ((original_getItemMetadata) => (row) => {
            const item = slickData.getItem(row);
            let metaData = original_getItemMetadata(row);

            if (typeof this._model.getItemMetadata === 'function') {
                metaData = this._model.getItemMetadata(metaData, row, item, slickData);
            }

            return metaData;
        })(slickData.getItemMetadata);

        if (isSelectable) {
            slickGrid.setSelectionModel(new Slick.RowSelectionModel());
            //keep the grid selection in sync
            slickData.syncGridSelection(slickGrid, false);
        }

        this._slickGrid = slickGrid;
        this._slickData = this._slickGrid.getData();

        this._slickData.onRowCountChanged.subscribe(this._onRowCountChange.bind(this));
        this._slickData.onRowsChanged.subscribe(this._onRowsChanged.bind(this));
        this._slickGrid.onClick.subscribe(this._onGridClick.bind(this));
        this._slickGrid.render();
        return slickGrid;
    }

    _resizeCanvas() {
        if (this._slickGrid) {
            this._slickGrid.resizeCanvas();
        }
    }

    _setLoading(isLoading) {
        this._table.classList.toggle("loading", isLoading);
    }

    _setIsEmpty(state) {
        this._table.classList.toggle("empty", !!state);
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /* START PUBLIC API RENDERER METHODS        */
    /********************************************/

    getSelectedRows() {
        return this._slickGrid.getSelectedRows();
    }

    setSelectedRows(rows) {
        this._slickGrid.setSelectedRows([].concat(rows));
    }

    updateTableHeight(rowsAmount) {
        const rowHeight = this._griOptions.rowHeight;
        let visibleRows = Math.min(this._maxVisibleRows, rowsAmount || this._noRowsHeight);

        this._table.style.height = rowHeight + visibleRows * rowHeight + "px";

        this._resizeCanvas();
    }

    checkIsEmpty() {
        const gridData = this._model.getData().filter( item => !isNaN(this._slickData.getRowById(item.id)));
        this._setIsEmpty(!gridData.length);
    }

    /********************************************/
    /* END PUBLIC API RENDERER METHODS          */
    /*******************************************/
}

module.exports = DatatableRenderer;
